import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import Page from '@lib/components/v2/Page';
import { Form } from 'calidation';
import { getAge } from '@lib/Utils';
import { isAgeEligible, selectEligibleAge } from '@js/domain/ageEligibility';
import { AgeReview } from '@lib/components/v2/AgeReview';
import parse from 'html-react-parser';
import { localizedString } from '@languages';
import Message from '@lib/components/v2/Message';
import {
  DATA_CHECK_ATTEMPT_COUNT,
  ENABLE_CONFIRM_AGE,
  FLOW_V2_DATA_CHECK_SELECT_DIFF_ID_EXIT_BUTTON_ENABLED
} from '@spotMobileConfig';
import { Error500 } from '../../errors';
import APIs from '../../../services/APIs';
import { DataCheckContent, ChooseAnotherId } from '../../components/Contents';
import { ExitButton } from './ExitButton';

class Datacheck extends Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  /**
   * Return the component's initial state
   *
   */
  getInitialState() {
    const state = {
      loading: true,
      error: null,
      data: {},
      chooseDiffId: false,
      showFailedScreen: false,
      showAfterExitScreen: false
    };

    return state;
  }

  componentDidMount() {
    this.triggerDataCheck();
  }

  handleChange = (field, value) => {
    this.setState(({ data }) => ({
      data: {
        ...data,
        [field]: value
      }
    }));
  };

  triggerDataCheck = async () => {
    const { documentId, isFlowV2DiffId, onNextStep, on500Error, eligibleAge } = this.props;
    const { data = {} } = this.state;

    if (!isEmpty(data)) {
      const age = getAge(data.dateOfBirth, true);
      if (ENABLE_CONFIRM_AGE && !isAgeEligible(age, eligibleAge)) {
        const error = {
          component: AgeReview,
          props: {
            eligibleAge,
            onTryAgain: () => this.setState({ error: null })
          }
        };
        this.setState({ error });
        return;
      }
    }

    this.setState({ loading: true, showFailedScreen: false });

    APIs.callDataCheck({ data, documentId })
      .then(async ({ status, dataCheckInputs = null }) => {
        if (status !== 'success') {
          if (isFlowV2DiffId) {
            onNextStep();
            return;
          }
          const temp = { ...dataCheckInputs };

          this.setState({
            data: { ...temp },
            loading: false,
            showFailedScreen: true
          });
          let count = 1;
          if (!sessionStorage.getItem('flow_v2_datacheck_count')) {
            sessionStorage.setItem('flow_v2_datacheck_count', count);
          } else {
            let count2 = sessionStorage.getItem('flow_v2_datacheck_count');
            count2 = parseInt(count2, 10);
            sessionStorage.setItem('flow_v2_datacheck_count', count2 + 1);
            count = count2 + 1;
          }
          if (count >= DATA_CHECK_ATTEMPT_COUNT) {
            this.setState({
              chooseDiffId: true,
              currentDoc: {
                type: dataCheckInputs.cardType
              }
            });
            // prompt to choose another ID
            // this.props.onNextStep();
          }
          return;
        }
        onNextStep();
      })
      .catch(({ message }) => {
        console.error(message);
        const error = {
          component: Error500,
          props: {
            onTryAgain: () => {
              on500Error();
            }
          }
        };
        this.setState({ error, loading: false });
      });
  };

  handleFormUpdate = ({ fields, isValid }) => {
    if (Object.keys(fields).length > 0) {
      this.setState({
        hasFormErrors: !isValid
      });
    }
  };

  handleChooseDiffId = (newDoc) => {
    const { onChooseDiffId } = this.props;
    onChooseDiffId(newDoc);
  };

  handleClickExit = () => {
    this.setState({ showAfterExitScreen: true, showFailedScreen: false });
  };

  render() {
    const {
      loading = true,
      error,
      data,
      hasFormErrors,
      chooseDiffId = false,
      currentDoc,
      showFailedScreen,
      showAfterExitScreen
    } = this.state;

    const { component: Error, props: errorProps } = error || {};

    const buttons = [];

    if (!loading) {
      buttons.push({
        label: 'Confirm Data',
        type: 'submit',
        disabled: hasFormErrors
      });
    }

    const selectDiffIdTitle = localizedString('dataCheck.FLOW_V2_DATA_CHECK_SELECT_DIFF_ID_TITLE');

    if (showFailedScreen && !loading) {
      const issueButtons = [
        {
          label: chooseDiffId
            ? localizedString('dataCheck.FLOW_V2_DATA_CHECK_SELECT_DIFF_ID_BUTTON_TEXT')
            : localizedString('dataCheck.FLOW_V2_DATA_CHECK_REVIEW_DATA_BUTTON_TEXT'),
          type: 'submit',
          onClick: () => this.setState({ showFailedScreen: false }),
          dataTestId: 'datacheck-confirm-prompt-cta'
        }
      ];

      return (
        <div>
          <Message
            buttons={issueButtons}
            title={
              chooseDiffId
                ? selectDiffIdTitle
                : localizedString('dataCheck.FLOW_V2_DATA_CHECK_NO_MATCH_TITLE')
            }
            issue
          >
            {chooseDiffId
              ? parse(localizedString('dataCheck.FLOW_V2_DATA_CHECK_SELECT_DIFF_ID_MESSAGE'))
              : parse(localizedString('dataCheck.FLOW_V2_DATA_CHECK_NO_MATCH_INFO_MESSAGE'))}
          </Message>
          {chooseDiffId && FLOW_V2_DATA_CHECK_SELECT_DIFF_ID_EXIT_BUTTON_ENABLED && (
            <ExitButton onClick={this.handleClickExit} />
          )}
        </div>
      );
    }

    if (showAfterExitScreen) {
      return (
        <Message title={selectDiffIdTitle} issue>
          {parse(localizedString('dataCheck.FLOW_V2_DATA_CHECK_SELECT_DIFF_ID_EXIT_MESSAGE'))}
        </Message>
      );
    }

    if (chooseDiffId && !loading) {
      return <ChooseAnotherId currentDoc={currentDoc} onNextStep={this.handleChooseDiffId} />;
    }

    return (
      <Form onUpdate={this.handleFormUpdate} onSubmit={this.triggerDataCheck}>
        {Error && <Error {...errorProps} />}
        <Page title="Data Check" buttons={buttons} onGoBack={this.handleGoBack}>
          <DataCheckContent onChange={this.handleChange} isLoading={loading} data={data} />
        </Page>
      </Form>
    );
  }
}

Datacheck.propTypes = {
  documentId: PropTypes.string,
  isFlowV2DiffId: PropTypes.bool,
  appConfig: PropTypes.object,
  onNextStep: PropTypes.func,
  on500Error: PropTypes.func,
  onChooseDiffId: PropTypes.func,
  eligibleAge: PropTypes.number
};

export default connect(mapStateToProps, null)(Datacheck);

/**
 * Map the store's state to the component's props
 * @param  {Object} state
 * @return {Object}
 */
function mapStateToProps(state) {
  const { appConfig } = state;
  return {
    appConfig,
    eligibleAge: selectEligibleAge(state)
  };
}
